<script setup lang="ts">
import FooterLogos from './FooterLogos.vue';
import OrganizationalSolutions from './OrganizationalSolutions.vue';
import useBillTracking from '@composable/useBillTracking';
import { type ReserveInfo } from '@utils/types';
import { NuxtLink } from '#components';

defineProps<IProps>();
const i18n = useI18n();
const onTracking = useBillTracking();
const siteConfigs = useSiteConfigs();
interface IProps {
    compact?: boolean;
    isHomePage?: boolean;
}
const { isAirplaneTicket } = useWhiteLabel();
const reservesList = computed<ReserveInfo[]>(() => siteConfigs.reserves);
const layoutStore = useLayoutStore();
const { supportNumber, isExtraFeaturesEnabled } = classToRefs(layoutStore);
const footerLinks = computed(
    () =>
        siteConfigs?.footerVariantLinks.map((link: any) => {
            if (link.href.startsWith('/')) {
                return {
                    is: NuxtLink,
                    props: {
                        to: link.href,
                    },
                    title: link.title,
                };
            } else {
                return {
                    is: 'a',
                    props: {
                        href: link.href,
                        target: '_blank',
                        rel: link.rel || undefined,
                    },
                    title: link.title,
                };
            }
        }) || []
);

const footerDescription = computed(
    () => siteConfigs?.footerDescription || i18n.t('FooterLinks.aboutUs')
);
</script>
<template>
    <div class="footer-links-root" :class="{ compact }">
        <div class="top-row">
            <div
                class="right-half"
                :class="{ alone: compact || isAirplaneTicket }"
            >
                <div class="top">
                    <mr-button
                        size="big"
                        variant="transparent"
                        class="fix-button"
                        link
                        :href="`tel:+9821${supportNumber}`"
                        rel="nofollow"
                    >
                        <span>تلفن پشتیبانی ۲۴ ساعته:</span>
                        <span>021-{{ supportNumber }}</span>
                    </mr-button>
                    <mr-button
                        size="big"
                        variant="transparent"
                        class="fix-button"
                        link
                        :href="`mailto:${siteConfigs.email}`"
                        rel="nofollow"
                    >
                        <span>ایمیل پشتیبانی:</span>
                        <span>{{ siteConfigs.email }}</span>
                    </mr-button>
                </div>
                <mr-divider color="var(--cool-gray-100)" class="no-compact" />
                <div class="bottom">
                    <mr-button
                        v-for="item in reservesList"
                        :key="item.type"
                        :href="item.link"
                        link="nuxt"
                        class="reserve-button"
                        size="big"
                        variant="transparent"
                    >
                        {{ item.title }}
                    </mr-button>
                </div>

                <mr-button
                    v-if="!compact && isExtraFeaturesEnabled"
                    size="big"
                    variant="transparent"
                    class="fix-button"
                    @click="onTracking"
                >
                    پیگیری و کنسلی بلیط
                </mr-button>
            </div>
            <OrganizationalSolutions
                v-if="!compact && isExtraFeaturesEnabled"
            />
            <mr-button
                v-else
                size="big"
                variant="transparent"
                no-wrap
                class="nowrap"
                @click="onTracking"
            >
                پیگیری و کنسلی بلیط
            </mr-button>
        </div>
        <mr-accordion class="footer-accordion">
            <template #title>
                <mr-typography
                    size="large"
                    weight="700"
                    color="var(--cool-gray-800)"
                >
                    خدمات سفر
                </mr-typography>
            </template>
            <template #toggle="{ currentIcon }">
                <mr-button
                    size="medium"
                    :icon-button="{
                        icon: currentIcon,
                        color: 'var(--blue-700)',
                    }"
                    variant="transparent"
                />
            </template>
            <div class="accordion-items">
                <NuxtLink
                    v-for="item in reservesList"
                    :key="item.type"
                    :to="item.link"
                    class="item reserve-item"
                >
                    {{ item.title }}
                </NuxtLink>
            </div>
        </mr-accordion>
        <mr-divider color="var(--cool-gray-100)" class="mobile" />
        <mr-accordion class="footer-accordion">
            <template #title>
                <mr-typography
                    size="large"
                    weight="700"
                    color="var(--cool-gray-800)"
                >
                    راهنمایی و پشتیبانی
                </mr-typography>
            </template>
            <template #toggle="{ currentIcon }">
                <mr-button
                    :icon-button="{
                        icon: currentIcon,
                        color: 'var(--blue-700)',
                    }"
                    size="medium"
                    variant="transparent"
                />
            </template>
            <div class="accordion-items">
                <component
                    :is="link.is"
                    v-for="link in footerLinks"
                    :key="link.title"
                    v-bind="link.props"
                    class="item footer-link"
                >
                    {{ link.title }}
                </component>
            </div>
        </mr-accordion>
        <mr-divider color="var(--cool-gray-100)" class="mobile" />
        <div class="body">
            <div class="help">
                <b class="header">راهنمایی و پشتیبانی</b>
                <div class="links-container">
                    <component
                        :is="link.is"
                        v-for="link in footerLinks"
                        :key="link.title"
                        v-bind="link.props"
                        class="footer-link"
                    >
                        {{ link.title }}
                    </component>
                </div>
            </div>
            <div class="intro">
                <b class="header">
                    {{ i18n.t('FooterLinks.introduction') }}
                </b>
                <mr-typography weight="500" color="var(--cool-gray-600)">
                    {{ footerDescription }}
                </mr-typography>
                <FooterLogos :is-home-page :site-configs />
            </div>
        </div>
        <mr-divider color="var(--cool-gray-100)" class="no-compact" />
        <div class="bottom-row">
            <mr-typography
                size="small"
                weight="500"
                color="var(--cool-gray-600)"
                :align="{ desktop: 'right', mobile: 'center' }"
                grow
            >
                تمامی حقوق برای شرکت عتیق گشت اصفهان محفوظ است.
            </mr-typography>
            <div class="top-column">
                <mr-button
                    link
                    :href="`mailto:${siteConfigs.email}`"
                    variant="transparent"
                    size="big"
                >
                    {{ siteConfigs.email }}
                </mr-button>
                <div class="social">
                    <mr-button
                        v-for="network in siteConfigs.socialNetworks as any"
                        :key="network.href"
                        :aria-label="network.icon"
                        :href="network.href"
                        :rel="network.rel"
                        link
                        target="_blank"
                        variant="transparent"
                        :icon-button="{
                            icon: network.icon,
                            color: 'var(--blue-400)',
                        }"
                        size="big"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.footer-links-root {
    display: flex;
    flex-direction: column;
    @include f-gap(24px, column, $offset: 32px);
    @include mobile-only {
        @include f-gap(12px, column, $offset: 16px);
    }
    &.compact {
        .spacer {
            display: none;
        }
        .reserve-button {
            display: none;
        }
        .footer-accordion {
            display: none;
        }
        .mobile {
            display: none;
        }
        .body {
            display: none;
            .help {
                .header {
                    display: none;
                }
                .links-container {
                    display: none;
                }
            }
            .intro {
                display: none;
            }
        }
        .no-compact {
            display: none;
        }
    }
    .mobile {
        @include desktop-only {
            display: none;
        }
    }
    .top-row {
        display: flex;
        align-items: center;
        gap: 64px;
        @include mobile-only {
            flex-direction: column;
            gap: 16px;
        }
        > div {
            flex-basis: calc(50% - 32px);
            @include mobile-only {
                flex-basis: 100%;
            }
        }
        .nowrap {
            white-space: nowrap;
            flex-shrink: 0;
        }
        .right-half {
            display: flex;
            flex-direction: column;
            gap: 12px;
            @include mobile-only {
                width: 100%;
            }
            &.alone {
                flex-basis: 100%;
            }
            .top {
                display: flex;
                align-items: center;
                gap: 16px;
                @include mobile-only {
                    flex-direction: column;
                }
                .fix-button {
                    background: usePalette($p-gray, $p-100);
                    flex-shrink: 0;
                    @include mobile-only {
                        width: 100%;
                    }
                }
            }
            .bottom {
                display: flex;
                align-items: center;
                .reserve-button {
                    @include mobile-only {
                        display: none;
                    }
                }
            }
        }
        .spacer {
            flex-grow: 1;
            @include mobile-only {
                display: none;
            }
        }
    }
    .footer-accordion {
        @include desktop-only {
            display: none;
        }
        .accordion-items {
            display: flex;
            flex-wrap: wrap;
            .item {
                flex-basis: 50%;
                max-width: 50%;
                &.reserve-item {
                    @include tg-m($w7, usePalette());
                }
                &.footer-link {
                    @include tg-m($w5, usePalette($p-cool-gray, $p-600));
                }
            }
        }
    }
    .body {
        display: flex;
        gap: 32px;
        @include mobile-only {
            gap: 0;
        }
        .header {
            @include tg-l($w7, usePalette($p-cool-gray, $p-800));
        }
        .help {
            display: flex;
            flex-direction: column;
            flex-shrink: 0;
            gap: 16px;
            @include mobile-only {
                display: none;
            }
            .links-container {
                display: flex;
                flex-direction: column;
                row-gap: 4px;
                column-gap: 32px;
                flex-wrap: wrap;
                max-height: 124px;
                width: 252px;
                .footer-link {
                    @include tg-m($w5, usePalette($p-cool-gray, $p-600));
                }
            }
            .buttons-container {
                display: flex;
                align-items: center;
                row-gap: 4px;
                column-gap: 32px;
                width: 252px;
                .footer-button {
                    cursor: pointer;
                    @include tg-m($w5, usePalette($p-cool-gray, $p-600));
                }
            }
        }
        .intro {
            display: flex;
            flex-direction: column;
            gap: 16px;
            max-width: 100%;
            overflow: auto;
        }
    }
    .bottom-row {
        display: flex;
        flex-wrap: wrap-reverse;
        align-items: center;
        .top-column {
            display: flex;
            @include f-gap(24px);
            @include mobile-only {
                width: 100%;
                justify-content: space-between;
            }
            .social {
                display: flex;
            }
        }
    }
}
</style>
